import React from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Loader2, AlertCircle } from 'lucide-react';

/**
 * SaveStatusPill — visual companion to `useDebouncedSave`.
 *
 * Renders nothing for "idle", a spinner for "saving", a green pill with
 * a check icon for "saved" (1.5s, then idle again), and a red error pill
 * when the save fails. Designed to live next to a form header so the
 * user always knows whether their work is persisted.
 *
 * Props:
 *  status — 'idle' | 'saving' | 'saved' | 'error'
 *  error?: string (only used when status === 'error')
 *
 * Variant defaults to light theme (white surface). For dark surfaces
 * pass `variant="dark"` to invert text colors.
 */
const SaveStatusPill = ({ status, error, variant = 'light' }) => {
  const { t } = useTranslation();

  if (status === 'idle') return null;

  if (status === 'saving') {
    return (
      <span
        data-testid="save-status-pill-saving"
        className={`inline-flex items-center gap-1.5 text-[11.5px] font-semibold ${
          variant === 'dark' ? 'text-white/55' : 'text-slate-500'
        }`}
      >
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
        {t('order_channels.saving', 'Saving…')}
      </span>
    );
  }

  if (status === 'error') {
    return (
      <span
        data-testid="save-status-pill-error"
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-rose-500/40 bg-rose-500/[0.08] text-rose-300 text-[11px] font-bold"
        title={error || ''}
        style={{ animation: 'nofee-pill-in 200ms ease-out both' }}
      >
        <AlertCircle className="w-3.5 h-3.5" />
        {t('save_status.error', 'Save failed')}
      </span>
    );
  }

  // status === 'saved'
  return (
    <span
      data-testid="save-status-pill-saved"
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-emerald-500/40 bg-emerald-500/[0.10] text-emerald-600 text-[11px] font-bold"
      style={{ animation: 'nofee-pill-in 200ms ease-out both' }}
    >
      <Check className="w-3.5 h-3.5" strokeWidth={2.4} />
      {t('order_channels.saved', 'Saved')}
    </span>
  );
};

export default SaveStatusPill;
